import { useState } from 'react';
import { getApiBase } from '../../lib/api';

/**
 * 智能 Drizzle 產生器：自然語言描述 → AI 產出 Drizzle Schema
 */
export function AiDrizzleTool() {
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = getApiBase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || !apiBase) {
      if (!apiBase) setError('請設定 VITE_API_BASE 後端網址');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/ai/drizzle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: description.trim() }),
      });
      const data = (await res.json()) as {
        success?: boolean;
        code?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(data.message ?? `錯誤 ${res.status}`);
        setCode('');
        return;
      }
      setCode(data.code ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : '請求失敗');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              用一句話描述想要的資料表
            </label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="例如：我要一個存使用者訂單的表格，有訂單編號、金額、狀態、建立時間"
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              disabled={!apiBase}
            />
          </div>
          {!apiBase && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              請在環境變數設定 VITE_API_BASE 指向後端 API 網址。
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !apiBase}
            className="rounded bg-primary px-4 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? '產生中…' : '產生 Drizzle Schema'}
          </button>
        </form>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          產出程式碼
        </h2>
        {code ? (
          <pre className="overflow-x-auto rounded bg-white p-4 font-mono text-sm dark:bg-gray-900">
            <code>{code}</code>
          </pre>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            上方輸入描述並送出後，將顯示 AI 產生的 Drizzle Schema。
          </p>
        )}
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          用自然語言描述資料表結構（例如「使用者訂單，要有編號、金額、狀態」），由
          Cloudflare Workers AI（Llama）產出 Drizzle ORM 的 pgTable Schema
          程式碼，方便快速建表或對照既有 Schema。
        </p>
      </section>
    </>
  );
}
